import React, { FC, Fragment, useEffect, useState } from 'react';
import { Grid, TextField } from '@mui/material';
import { QuestionView, ResponseView } from '@views';
import { useNavigationEligibility } from '@components/SurveyDrawer/NavigationEligibilityContextProvider';

interface FreeTextResponseProps {
  currentQuestion: QuestionView;
}

const readValues = (question: QuestionView): Record<number, string> => {
  const values: Record<number, string> = {};
  question.responses.forEach((r) => {
    if (r.responseType === 'FreeText') values[r.id] = r.textResponse ?? '';
  });
  return values;
};

export const FreeTextResponse: FC<FreeTextResponseProps> = ({ currentQuestion }) => {
  const { triggerEligibility } = useNavigationEligibility();

  // Keep what is on screen bound to what will actually be submitted. Left uncontrolled the two
  // drift apart in both directions: an answer already recorded renders as an empty box when the
  // user navigates back to it, and text the model no longer holds stays visible as though it
  // were still an answer.
  const [values, setValues] = useState<Record<number, string>>(() => readValues(currentQuestion));

  useEffect(() => {
    // Re-read the question when it changes (e.g. the user navigates next/previous)
    setValues(readValues(currentQuestion));
  }, [currentQuestion]);

  const handleFreeTextResponse = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    option: ResponseView
  ) => {
    // Enforce numeric validation for the survey responses
    // Check question IDs 210, 220 and 221
    // Don't allow any non-numeric input for these questions, completely stripping out any non-numeric characters.
    // TODO: Change how we handle numeric validation. This is a (bad) temporary solution.
    const value = [210, 220, 221].includes(currentQuestion.id)
      ? e.target.value.replace(/\D+/g, '') // Remove all non-digit characters
      : e.target.value;

    option.textResponse = value;
    setValues((current) => ({ ...current, [option.id]: value }));
    triggerEligibility();
  };

  return (
    <Fragment>
      {currentQuestion.responses.some((opt) => opt.responseType === 'FreeText') &&
        currentQuestion.responses.map((opt) => {
          if (opt.responseType === 'FreeText') {
            return (
              <Grid
                key={opt.id}
                size={{ xs: 12 }}
                sx={{
                  flexWrap: 'wrap',
                  alignContent: 'center',
                  justifyContent: 'flex-start',
                  display: 'flex',
                  gap: 2,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
                <TextField
                  sx={{ minWidth: '100%' }}
                  onChange={(e) => handleFreeTextResponse(e, opt)}
                  label={opt.text}
                  variant="outlined"
                  value={values[opt.id] ?? ''}
                  fullWidth
                />
              </Grid>
            );
          }
          return null;
        })}
    </Fragment>
  );
};
